/**
 * Temporary one-off code for the US2119019 Group 2 foundation diagnostic. It calls the dash-bff
 * assistant diagnostic route outside the Redux data flow on purpose, and is replaced (not extended)
 * by the assistant panel work in US2105994. Not a pattern for new features.
 */

// externals
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

// libraries
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { getCurrentAxiosRequestInterceptor } from '@makemydeal/dr-shared-network';
import { X_DR_DEALER_ID } from '@makemydeal/dr-shared-bff-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

export const ASSISTANT_DIAGNOSTIC_PATH = '/ai/assistant/diagnostic';
export const ASSISTANT_DIAGNOSTIC_TIMEOUT_MS = 15000;

const MAX_FIELD_LENGTH = 256;
const CODE_PATTERN = /^[a-z][a-z_]{0,63}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DiagnosticState =
    | { status: 'idle' }
    | { status: 'pending' }
    | { status: 'success'; stage: string; buildId: string; requestId: string }
    | { status: 'error'; code: string; requestId?: string; httpStatus?: number };

const IDLE: DiagnosticState = { status: 'idle' };
const PENDING: DiagnosticState = { status: 'pending' };

const isBoundedString = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && value.length <= MAX_FIELD_LENGTH;

const isPositiveInteger = (value: unknown): value is number => Number.isInteger(value) && (value as number) > 0;

const isUuid = (value: unknown): value is string => typeof value === 'string' && UUID_PATTERN.test(value);

const requestIdFrom = (value: unknown) => (isUuid(value) ? { requestId: value } : {});

const toSuccessState = (data: any): DiagnosticState => {
    if (
        data &&
        typeof data === 'object' &&
        data.kind === 'diagnostic' &&
        isBoundedString(data.stage) &&
        isBoundedString(data.buildId) &&
        isUuid(data.requestId)
    ) {
        return { status: 'success', stage: data.stage, buildId: data.buildId, requestId: data.requestId };
    }
    return { status: 'error', code: 'invalid_response', ...requestIdFrom(data?.requestId) };
};

const toErrorState = (error: unknown, timedOut: boolean): DiagnosticState => {
    if (timedOut) {
        return { status: 'error', code: 'timeout' };
    }
    if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        const message = (data as any)?.message;
        const traceId = (data as any)?.traceId;
        return {
            status: 'error',
            code: typeof message === 'string' && CODE_PATTERN.test(message) ? message : `http_${status}`,
            ...requestIdFrom(traceId),
            httpStatus: status
        };
    }
    return { status: 'error', code: axios.isAxiosError(error) ? 'network' : 'client_error' };
};

export const useAssistantDiagnostic = () => {
    const [state, setState] = useState<DiagnosticState>(IDLE);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const controllerRef = useRef<AbortController | undefined>(undefined);

    useEffect(
        () => () => {
            controllerRef.current?.abort();
        },
        []
    );

    const run = useCallback(async () => {
        if (controllerRef.current) {
            return;
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, ASSISTANT_DIAGNOSTIC_TIMEOUT_MS);
        setState(PENDING);

        let next: DiagnosticState;
        try {
            const url = apiConfigHelper.buildBffApiUrl(
                MANAGER_VIEW_APP_PREFIX,
                locHrefUtil.getLocHref(),
                ASSISTANT_DIAGNOSTIC_PATH
            ).url;
            const { headers } = await getCurrentAxiosRequestInterceptor()({ url });
            if (!headers) {
                throw new Error('the auth interceptor did not recognize the BFF url');
            }
            const response = await axios.post(
                url,
                {},
                {
                    headers: {
                        ...headers,
                        ...(isPositiveInteger(dealerId) ? { [X_DR_DEALER_ID]: String(dealerId) } : {}),
                        'Content-Type': 'application/json'
                    },
                    signal: controller.signal
                }
            );
            next = toSuccessState(response.data);
        } catch (error) {
            next = toErrorState(error, timedOut);
        } finally {
            clearTimeout(timer);
            controllerRef.current = undefined;
        }

        setState(next);
    }, [dealerId]);

    return { state, run };
};
